import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Lock, ShieldCheck, Timer } from "lucide-react";
import Countdown from "@/components/Countdown";
import Reveal from "@/components/landing/Reveal";
import AnimatedProgress from "@/components/landing/AnimatedProgress";
import HeroAmbientVideo from "@/components/landing/HeroAmbientVideo";
import CursorSpotlight from "@/components/landing/CursorSpotlight";
import AnimatedGrid from "@/components/landing/AnimatedGrid";
import FounderVideo from "@/components/landing/FounderVideo";
import { renderTitle } from "@/components/landing/utils";
import { LANDING } from "@/constants/testIds";

export default function HeroSection({ hero, heroStats, roundConfig, metrics }) {
  const spotsLeft = metrics ? Math.max(0, (roundConfig?.spots_total || 0) - (metrics.spots_committed || 0)) : null;
  const eyebrow = hero?.eyebrow || "Expansión Rubí · Participación privada";
  const title = hero?.title || "Invierte en la próxima etapa de <em>expansión</em> de Rubí Perfumería.";
  const subtitle = hero?.subtitle || "Participa en proyectos seleccionados de nuevas aperturas respaldados por la operación, infraestructura y experiencia de una marca que continúa creciendo en México.";
  const ctaLabel = hero?.cta_label || "Conocer Proyecto Diamante";
  const ctaHref = hero?.cta_href || "#proyectos";
  const ctaSecondary = hero?.cta_secondary_label || "Evaluar mi participación";

  return (
    <section className="relative overflow-hidden section-dark isolate pt-6 md:pt-8 pb-16 md:pb-28">
      {/* Ambient background video (CMS-driven) */}
      <HeroAmbientVideo
        src={hero?.ambient_video_url}
        poster={hero?.ambient_video_poster}
        opacity={hero?.ambient_video_opacity ?? 0.28}
      />

      {/* Cursor spotlight — modern interaction */}
      <CursorSpotlight />

      {/* Animated tech grid — modern texture */}
      <AnimatedGrid opacity={0.5} cellSize={60} />

      {/* Ambient gold glows */}
      <div className="gold-glow" style={{ width: 620, height: 620, top: -180, right: -140 }} />
      <div className="gold-glow" style={{ width: 480, height: 480, bottom: -140, left: -160, opacity: 0.6 }} />
      <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />

      <div className="relative max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 pt-10 md:pt-24">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14 items-center">
          <div className="min-w-0">
            <Reveal delay={40}>
              <span className="pill pill-gold uppercase tracking-[0.18em] !text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFBF57] animate-pulse" />
                {eyebrow}
              </span>
            </Reveal>
            <Reveal delay={160}>
              <h1 className="mt-6 font-display text-[38px] sm:text-[44px] leading-[1.04] md:text-[62px] lg:text-[74px] text-[#0A0A0A] break-words">
                {renderTitle(title)}
              </h1>
            </Reveal>
            <Reveal delay={280}>
              <p className="mt-6 text-[#3F3F46] text-base md:text-lg leading-relaxed max-w-2xl">
                {subtitle}
              </p>
            </Reveal>
            <Reveal delay={400}>
              <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center">
                <a href={ctaHref} data-testid="hero-cta-proyectos" className="btn-primary justify-center">
                  {ctaLabel} <ArrowRight size={16} strokeWidth={1.75} />
                </a>
                <Link to="/calificacion" data-testid={LANDING.heroCtaForm} className="btn-outline justify-center">
                  {ctaSecondary} <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={520}>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-[#52525B]">
                <div className="flex items-center gap-2"><Lock size={12} className="text-[#FFBF57]" /> Proceso confidencial</div>
                <div className="flex items-center gap-2"><ShieldCheck size={12} className="text-[#FFBF57]" /> Estructura AEP formal</div>
                <div className="flex items-center gap-2"><Timer size={12} className="text-[#FFBF57]" /> Cupos limitados por proyecto</div>
              </div>
            </Reveal>
          </div>

          {/* Right side · Founder video + Countdown card */}
          <Reveal delay={200} scale className="relative">
            <div className="relative space-y-4">
              <FounderVideo
                videoUrl={roundConfig?.welcome_video_url}
                poster={roundConfig?.welcome_video_poster}
                duration={roundConfig?.welcome_video_duration}
                autoplay={roundConfig?.welcome_video_autoplay ?? false}
              />

              <div className="relative card-onyx on-onyx p-5 md:p-7 pulse-glow">
                <div className="flex items-center justify-between mb-5">
                  <span className="eyebrow">Convocatoria activa</span>
                  <span className="pill pill-gold">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FFBF57] animate-pulse" /> LIVE
                  </span>
                </div>
                <div className="text-[11px] uppercase tracking-widest text-[#52525B] mb-2">Cierra en</div>
                <Countdown target={roundConfig?.closes_at} />

                <div className="hairline my-6" />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono">Cupos totales</div>
                    <div className="mt-1 text-3xl num-mono text-[#0A0A0A]">{roundConfig?.spots_total ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono">Comprometidos</div>
                    <div className="mt-1 text-3xl num-mono text-gold-gradient">
                      {metrics?.spots_committed ?? roundConfig?.spots_committed ?? "—"}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <AnimatedProgress
                      value={Math.min(
                        100,
                        ((metrics?.spots_committed ?? roundConfig?.spots_committed ?? 0) /
                          Math.max(1, roundConfig?.spots_total ?? 1)) *
                          100
                      )}
                    />
                  </div>
                  <div className="col-span-2 text-xs text-[#3F3F46]">
                    Quedan <span className="text-[#0A0A0A] font-semibold num-mono">{spotsLeft ?? "—"}</span> participaciones disponibles.
                  </div>
                </div>

                <Link to="/calificacion" className="btn-primary w-full justify-center mt-6" data-testid="hero-countdown-cta">
                  Aplicar ahora <ArrowUpRight size={14} />
                </Link>
                <div className="text-[10px] text-[#52525B] text-center mt-2 font-mono uppercase tracking-widest">
                  Toma 2 minutos · No es un compromiso
                </div>
              </div>
            </div>
          </Reveal>
        </div>

      </div>
    </section>
  );
}
